const ISSUE_STATUSES_KEY = 'issue_statuses'
const IS_DEFAULT_KEY = 'is_default'
const TRANSFER_TO_KEY = 'transfer_to_id'

export const toRow = status => ({
  key: String(status.id),
  id: status.id,
  name: status.name,
  color: status.color,
  category: status.category,
  position: status.position,
  isDefault: status.is_default,
  issuesCount: status.issues_count ?? 0
})

export const orderedFor = (rows, category) => rows
  .filter(row => !row.destroyed && row.category === category)
  .sort((left, right) => left.position - right.position)

// Re-number a category's surviving rows to 0..n-1 (position order) so a deletion
// Never leaves a gap that would let handleAdd reuse an already-taken position.
const reindexCategory = (rows, category) => {
  const positionByKey = new Map(orderedFor(rows, category).map((row, index) => [row.key, index]))
  return rows.map(row => {
    if (!positionByKey.has(row.key))
      return row
    return { ...row, position: positionByKey.get(row.key) }
  })
}

export const removeOrFlag = (rows, row) => {
  if (!row.id)
    return reindexCategory(rows.filter(item => item.key !== row.key), row.category)
  const flagged = rows.map(item => item.key === row.key ? { ...item, destroyed: true } : item)
  return reindexCategory(flagged, row.category)
}

export const flagTransfer = (rows, key, transferValue) => {
  const target = rows.find(row => row.key === key)
  const flagged = rows.map(row => {
    if (row.key !== key)
      return row
    return { ...row, destroyed: true, transferToId: Number(transferValue) }
  })
  return reindexCategory(flagged, target.category)
}

export const applyMove = (rows, { key, from, to, index, defaultColor }) => {
  const next = rows.map(row => ({ ...row }))
  const moved = next.find(row => row.key === key)
  moved.category = to
  if (from !== to)
    moved.color = defaultColor
  const target = orderedFor(next, to).filter(row => row.key !== key)
  target.splice(index, 0, moved)
  target.forEach((row, position) => { row.position = position })
  orderedFor(next, from).forEach((row, position) => { row.position = position })
  return next
}

export const buildBatchPayload = rows => {
  const upserts = rows.filter(row => !row.destroyed).map(row => {
    const entry = {
      key: row.key, name: row.name, color: row.color, category: row.category, position: row.position
    }
    entry[IS_DEFAULT_KEY] = row.isDefault
    if (row.id)
      entry.id = row.id
    return entry
  })
  const deletions = rows.filter(row => row.destroyed && row.id).map(row => ({
    id: row.id, [TRANSFER_TO_KEY]: row.transferToId
  }))
  return { [ISSUE_STATUSES_KEY]: upserts, deletions }
}

const COMPARED_FIELDS = ['name', 'color', 'category', 'isDefault']

const isRowChanged = (row, initial) => {
  if (row.destroyed)
    return Boolean(row.id)
  const before = initial.get(row.id)
  if (!before)
    return true
  return COMPARED_FIELDS.some(field => before[field] !== row[field])
}

export const saveOutcome = (rows, initialRows) => {
  const initial = new Map(initialRows.filter(row => row.id).map(row => [row.id, row]))
  const changed = rows.filter(row => isRowChanged(row, initial))
  if (changed.length > 1)
    return 'mixed'
  const [row] = changed
  if (row?.destroyed)
    return 'deleted'
  if (row?.id && initial.get(row.id)?.category !== row.category)
    return 'recategorized'
  return 'updated'
}
